import { createSelector, createSlice } from '@reduxjs/toolkit';

import { fetchFgpGameData } from '@/features/game/store/thunks/fetch-fgp-game-data';
import { GameDataLoadingState } from '@/features/game/types';
import { GameData } from '@/features/providers/types';
import { RootState } from '@/store/store';

export type GameDataState = {
  loadingState: GameDataLoadingState;
  gameData?: GameData;
  error?: Record<string, unknown>;
  requestId?: string;
};

const initialState: GameDataState = {
  loadingState: GameDataLoadingState.NotStarted,
};

const gameDataSlice = createSlice({
  name: 'gameData',
  initialState,
  reducers: {
    setGameData: (state, action: { payload: GameData }) => {
      state.gameData = action.payload;
      state.loadingState = GameDataLoadingState.Completed;
      state.requestId = action.payload?.requestId;
    },
    resetGameData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFgpGameData.pending, (state) => {
      state.loadingState = GameDataLoadingState.Loading;
    });
    builder.addCase(fetchFgpGameData.fulfilled, (state, { payload }) => {
      state.loadingState = GameDataLoadingState.Completed;
      state.gameData = payload;
      state.requestId = payload?.requestId;
    });
    builder.addCase(fetchFgpGameData.rejected, (state, action) => {
      state.loadingState = GameDataLoadingState.Failed;
      state.error = action.payload as Record<string, unknown>;
    });
  },
});

export const { setGameData, resetGameData } = gameDataSlice.actions;

export const gameDataRootSelector = (state: RootState) => state.gameData;
export const gameDataLoadingSelector = createSelector(
  gameDataRootSelector,
  (data) => data.loadingState,
);
export const gameDataSelector = createSelector(
  gameDataRootSelector,
  (data) => data.gameData,
);
export const gameErrorSelector = createSelector(
  gameDataRootSelector,
  (data) => data.error,
);
export const requestIdSelector = createSelector(
  gameDataRootSelector,
  (data) => data.requestId,
);

export const sessionIdSelector = createSelector(
  gameDataRootSelector,
  (data) => {
    const launchDetails = data.gameData?.launchDetails;
    // Check if launchDetails has sessionid property
    if (launchDetails && 'sessionid' in launchDetails) {
      return launchDetails.sessionid as string;
    }
    return undefined;
  },
);

export const gameDataReducer = gameDataSlice.reducer;
